import { Field, Form, Formik, FormikHelpers } from 'formik'
import { FC, ReactElement, useCallback, useEffect, useState } from 'react'
import {
  GoogleReCaptchaProvider,
  useGoogleReCaptcha
} from 'react-google-recaptcha-v3'
import { Link, useHistory } from 'react-router-dom'
import * as yup from 'yup'
import Button from '../../components/button'
import Input from '../../components/input'
import SocialButton, { SocialLoginSuccess } from '../../components/socialButton'
import config from '../../config'
import { useAlertHandler } from '../../hooks/alertHandler'
import { useAuth } from '../../hooks/auth'
import { createErrorResponse } from '../../utils/createError'
import { RoutesEnum } from '../../utils/routesEnum'
import { SocialButtons } from '../register/style'
import LoginSkeleton from './skeleton'

const schema = yup.object().shape({
  email: yup.string().email('Email inválido').required('Email obrigatório'),
  password: yup.string().min(6, 'Senha minimia 6 caracteres').required('Senha obrigatória')
})

const Login: FC = (): ReactElement => {
  const initialValues = {
    email: '',
    password: ''
  }

  const history = useHistory()

  const { accessToken, status, login, loginSocial } = useAuth()
  const [loginSocialVia, setLoginSocialVia] = useState(null)
  const { executeRecaptcha } = useGoogleReCaptcha()
  const { showError } = useAlertHandler()

  useEffect(() => {
    if (status === 'logged' && accessToken) {
      history.push(RoutesEnum.PROCESS_EXAMS)
    }
  }, [status, accessToken])

  const submit = async (
    values: typeof initialValues,
    helpers: FormikHelpers<typeof initialValues>
  ): Promise<void> => {
    helpers.setSubmitting(true)
    try {
      const captcha = executeRecaptcha ? await executeRecaptcha() : undefined
      await login(values.email, values.password, captcha)
    } catch { }
    helpers.setSubmitting(false)
  }

  const handleSocialLogin = useCallback((type) => {
    return async (res: SocialLoginSuccess) => {
      setLoginSocialVia(type)
      try {
        const captcha = executeRecaptcha ? await executeRecaptcha() : undefined
        await loginSocial(
          type,
          type === 'google' ? res._token.idToken || '' : res._token.accessToken,
          captcha
        )
      } catch {}
      setLoginSocialVia(null)
    }
  }, [setLoginSocialVia, loginSocial, executeRecaptcha])

  const handleSocialLoginFailure = useCallback(() => {
    if (loginSocialVia) {
      setLoginSocialVia(null)
      const error = createErrorResponse('Não conseguimos realizar o login social.')
      showError(error)
    }
  }, [loginSocialVia, setLoginSocialVia, showError])

  return (
    <LoginSkeleton>
      {!navigator.userAgent.includes('Prerender') && (
        <Formik
          validationSchema={schema}
          initialValues={initialValues}
          onSubmit={submit}
        >
          {({ isSubmitting, isValid }) => (
            <Form>
              <SocialButtons>
                <SocialButton
                  appId={config.FACEBOOK_APP_ID}
                  provider='facebook'
                  type='facebook'
                  scope='email'
                  autoLogin={false}
                  isLoading={loginSocialVia === 'facebook'}
                  disabled={loginSocialVia === 'google'}
                  onLoginSuccess={handleSocialLogin('facebook')}
                  onLoginFailure={handleSocialLoginFailure}
                />
                <SocialButton
                  appId={config.GOOGLE_CLIENT_ID}
                  provider='google'
                  type='google'
                  autoLogin={false}
                  isLoading={loginSocialVia === 'google'}
                  disabled={loginSocialVia === 'facebook'}
                  onLoginSuccess={handleSocialLogin('google')}
                  onLoginFailure={handleSocialLoginFailure}
                />
              </SocialButtons>
              <Field
                component={Input}
                name='email'
                label='Email'
                type='email'
              />
              <Field
                component={Input}
                label='Senha'
                type='password'
                name='password'
                info={<Link to={RoutesEnum.FORGOT_PASSWORD}>Esqueci minha senha</Link>}
              />
              <div className='buttons'>
                <Button
                  isLoading={isSubmitting}
                  disabled={!!loginSocialVia || !isValid}
                  block
                  type='submit'
                >
                  LOGAR
                </Button>
                <br />
                <Button
                  to={RoutesEnum.REGISTER}
                  block
                  variant='link'
                >
                  Quero me cadastrar! Clique aqui
                </Button>
              </div>
            </Form>
          )}
        </Formik>
      )}
    </LoginSkeleton>
  )
}

const LoginWithReCaptcha: FC = (props): ReactElement => {
  return (
    <GoogleReCaptchaProvider reCaptchaKey={config.RECAPTCHA_TOKEN}>
      <Login {...props} />
    </GoogleReCaptchaProvider>
  )
}

export default LoginWithReCaptcha
